import {
  DateFormat,
  formatDate,
  formatTime,
  isDateAfter,
  MINUTE,
  ToastDuration,
  ToastType,
  useToast,
} from '@leland-dev/leland-ui-library';
import { type FC, type MouseEvent, useCallback, useState } from 'react';

import { LelandErrorCode } from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { LexSlug, useLex } from '../../context/LexContext';
import mxpnl from '../../utils/analytics/mixpanel';
import phog from '../../utils/analytics/posthog/posthog';
import { getClassUrl } from '../../utils/classes';
import { wasMutationSuccessful } from '../../utils/mutations';
import { useLoginModal } from '../LoginModal';
import { BaseEventCard } from '../common/BaseEventCard';
import { useSalesModal } from '../common/SalesModal';
import { useGuestSignupModal } from '../event/GuestSignupModal';
import { usePhoneNumberModal } from '../sms/PhoneNumberModal';

import {
  type ClassCardClassFragment,
  type ClassCardClassSessionFragment,
  useClassCardEnrollFreeEventMutation,
  useClassCardGuestSignupMutation,
  useClassCardResetPasswordMutation,
  useClassCardUnenrollFreeEventMutation,
  useClassCardUserQuery,
} from './__generated-gql-types__/ClassCard.generated';

interface ClassCardProps {
  classInfo: ClassCardClassFragment;
  classSession?: ClassCardClassSessionFragment;
  className?: string;
  onClick?: () => void;
  trackingPrefix: string;
}

const ClassCard: FC<ClassCardProps> = ({
  classInfo,
  classSession,
  onClick,
  trackingPrefix,
}) => {
  const { showToast } = useToast();
  const { isLexEnabled } = useLex();
  const { currentUser } = useAuth();
  const [showGuestSignupLoadingIndicator, setShowGuestSignupLoadingIndicator] =
    useState(false);

  const classPagePathname = getClassUrl(
    classInfo.slug ?? '',
    !!classInfo.isFreeEvent,
  );
  const isUserEnrolledInCurrentSession = classSession?.userIsEnrolled;
  const isUserBlocked = !!classInfo.coach?.coachingRelationship?.blockedByCoach;

  const [enrollFreeEventAction, { loading: enrollingLoading }] =
    useClassCardEnrollFreeEventMutation();
  const [unenrollFreeEventAction, { loading: unenrollLoading }] =
    useClassCardUnenrollFreeEventMutation();
  const { data: userData } = useClassCardUserQuery();
  const [guestSignupAction] = useClassCardGuestSignupMutation();
  const [resetPassword] = useClassCardResetPasswordMutation();

  const { setOpen: setLoginModalOpen, Modal: LoginModal } = useLoginModal();
  const { setOpen: setSalesModalOpen, Modal: SalesModal } = useSalesModal();
  const { setOpen: setGuestSignupModalOpen, Modal: GuestSignupModal } =
    useGuestSignupModal();
  const {
    openModalIfNecessary: openPhoneNumberModalIfNecessary,
    Modal: PhoneNumberModal,
  } = usePhoneNumberModal();

  const isGuestSignup =
    !currentUser && isLexEnabled(LexSlug.FEATURE_GUEST_EVENT_SIGNUP);

  const handleToggleEnroll = useCallback(
    async (
      guestEmail: string,
      guestFirstName: string,
      guestLastName: string,
    ) => {
      const customerEmail = currentUser?.email ?? guestEmail;
      if (isGuestSignup) {
        const guestUserRes = await guestSignupAction({
          variables: {
            email: guestEmail,
            firstName: guestFirstName,
            lastName: guestLastName,
            logUserIn: false,
          },
        });
        const guestUser = guestUserRes.data?.guestSignup;
        if (guestUser?.applicant?.id) {
          await resetPassword({
            variables: { email: guestEmail },
          });
        } else {
          if (
            !guestUserRes.errors?.some(
              (error) =>
                error.extensions?.errorCode === LelandErrorCode.EMAIL_IN_USE,
            )
          ) {
            throw new Error('Failed to signup guest user');
          }
        }
      }
      if (
        classInfo.isFreeEvent &&
        classSession?.id &&
        !enrollingLoading &&
        !unenrollLoading
      ) {
        if (isUserEnrolledInCurrentSession) {
          const res = await unenrollFreeEventAction({
            variables: {
              id: classSession.id,
            },
            optimisticResponse: {
              unenrollFreeEvent: {
                id: classSession.id,
                userIsEnrolled: false,
              },
            },
          });
          if (wasMutationSuccessful(res)) {
            mxpnl.track('Unenroll Free Event', {
              eventId: classInfo.classId,
              eventName: classInfo.className,
              sessionId: classSession.id,
              location: `${trackingPrefix} - ClassCard`,
            });
            phog.capture('free_event--unenroll', {
              eventId: classInfo.classId,
              eventName: classInfo.className,
              coachingSessionId: classSession.id,
              location: `${trackingPrefix} - ClassCard`,
            });
            showToast({
              type: ToastType.SUCCESS,
              duration: ToastDuration.NORMAL,
              message: 'You have successfully unenrolled.',
            });
          } else {
            showToast({
              type: ToastType.ERROR,
              duration: ToastDuration.NORMAL,
              message: 'Error unenrolling from session. Please try again.',
            });
          }
        } else {
          if (!customerEmail) {
            showToast({
              type: ToastType.ERROR,
              duration: ToastDuration.NORMAL,
              message: 'Error enrolling in event. Please try again.',
            });
            return;
          }
          const res = await enrollFreeEventAction({
            variables: {
              id: classSession.id,
              email: customerEmail,
            },
            optimisticResponse: {
              enrollFreeEvent: {
                id: classSession.id,
                userIsEnrolled: false,
              },
            },
          });
          if (wasMutationSuccessful(res)) {
            mxpnl.track('Enroll Free Event', {
              eventId: classInfo.classId,
              eventName: classInfo.className,
              sessionId: classSession.id,
              location: `${trackingPrefix} - ClassCard`,
            });
            phog.capture('free_event--enroll', {
              eventId: classInfo.classId,
              eventName: classInfo.className,
              coachingSessionId: classSession.id,
              location: `${trackingPrefix} - ClassCard`,
            });
            showToast({
              type: ToastType.SUCCESS,
              duration: ToastDuration.NORMAL,
              message: 'You have successfully enrolled.',
            });
            if (
              isGuestSignup ||
              isDateAfter(userData?.user?.createdAt ?? 0 + MINUTE, Date.now())
            ) {
              void setSalesModalOpen(true);
            } else {
              void openPhoneNumberModalIfNecessary();
            }
          } else {
            showToast({
              type: ToastType.ERROR,
              duration: ToastDuration.NORMAL,
              message: 'Error enrolling in event. Please try again.',
            });
          }
        }
      }
    },
    [
      classInfo.classId,
      classInfo.className,
      classInfo.isFreeEvent,
      classSession?.id,
      currentUser,
      enrollFreeEventAction,
      enrollingLoading,
      isGuestSignup,
      isUserEnrolledInCurrentSession,
      trackingPrefix,
      unenrollFreeEventAction,
      unenrollLoading,
      showToast,
      openPhoneNumberModalIfNecessary,
      setSalesModalOpen,
      userData?.user?.createdAt,
      guestSignupAction,
      resetPassword,
    ],
  );

  const handleButtonClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      if (classInfo.isFreeEvent && classSession) {
        e.preventDefault();
        if (userData?.user) {
          await handleToggleEnroll(
            userData.user.email,
            userData.user.firstName,
            userData.user.lastName,
          );
        } else {
          if (isLexEnabled(LexSlug.FEATURE_GUEST_EVENT_SIGNUP)) {
            setGuestSignupModalOpen(true);
          } else {
            setLoginModalOpen(true);
          }
        }
      }
      onClick?.();
    },
    [
      classInfo.isFreeEvent,
      classSession,
      onClick,
      userData?.user,
      handleToggleEnroll,
      setGuestSignupModalOpen,
      setLoginModalOpen,
      isLexEnabled,
    ],
  );

  return isUserBlocked ? null : (
    <>
      <BaseEventCard
        name={classInfo.className}
        date={classSession?.startAt}
        image={classInfo.imageLink}
        coaches={classInfo.coach}
        link={classPagePathname}
        isFree={classInfo.isFreeEvent ?? false}
        categories={classInfo.category ?? []}
        isEnrolled={!!isUserEnrolledInCurrentSession}
        isEnrolling={enrollingLoading}
        isUnenrolling={unenrollLoading}
        price={classInfo.price}
        onButtonClick={handleButtonClick}
        totalAttendees={classSession?.totalAttendees}
        attendeeFacePiles={classSession?.topFacePiles?.map((pictureLink) => ({
          pictureLink: pictureLink,
        }))}
        type={classInfo.__typename}
      />
      <PhoneNumberModal />
      {classSession ? (
        <SalesModal
          sectionInfo={`You are enrolled in "${classInfo.className}" on ${formatDate(
            classSession.startAt,
            DateFormat.LONG_STANDARD,
          )} at ${formatTime(classSession.startAt)}.`}
          sectionTitle="You have successfully enrolled."
        />
      ) : null}
      <LoginModal
        onApplicantLogin={async () => {
          setLoginModalOpen(false);
          if (currentUser) {
            await handleToggleEnroll(
              currentUser.email,
              currentUser.firstName,
              currentUser.lastName,
            );
          }
        }}
      />
      <GuestSignupModal
        onSignup={async ({ email, firstName, lastName }) => {
          setShowGuestSignupLoadingIndicator(true);
          await handleToggleEnroll(email, firstName, lastName);
          setGuestSignupModalOpen(false);
          setShowGuestSignupLoadingIndicator(false);
          return;
        }}
        onLoginClick={() => {
          setGuestSignupModalOpen(false);
          setLoginModalOpen(true);
        }}
        showLoadingIndicator={showGuestSignupLoadingIndicator}
      />
    </>
  );
};

export default ClassCard;
